import React, { useState, useEffect, useRef } from 'react';
import { Menu, Bell, Search, User, Settings, LogOut } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import './Navbar.css';
import useAuth from '../pages/useAuth';

const TopNavbar = ({ onToggleSidebar }) => {
     const location = useLocation();
     const navigate = useNavigate();
     const { profile, logout } = useAuth();
     const [showUserDropdown, setShowUserDropdown] = useState(false);
     const [showNotifications, setShowNotifications] = useState(false);
     const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
     const role = sessionStorage.getItem('user_role');

     const dropdownRef = useRef(null);
     const buttonRef = useRef(null);

     useEffect(() => {
          const handleResize = () => setIsMobile(window.innerWidth < 768);
          window.addEventListener('resize', handleResize);
          return () => window.removeEventListener('resize', handleResize);
     }, []);

     const BASE_URL = process.env.REACT_APP_BASE_URL;

     // fetch api totalSessions 
     const [totalSessions, setTotalSessions] = useState(0);
     const fetchTotalSessions = async () => {
          try {
               const response = await fetch(`${BASE_URL}/total-sessions`, {
                    method: 'GET',
                    headers: {
                         'Content-Type': 'application/json',
                         Authorization: `Bearer ${sessionStorage.getItem('token')}`,
                    },
               });
               const data = await response.json();
               setTotalSessions(data.totalSessions);
          } catch (error) {
               console.error('Error fetching total sessions:', error);
          }
     };

     useEffect(() => {
          fetchTotalSessions();
     }, []);

     useEffect(() => {
          const handleClickOutside = (event) => {
               if (
                    dropdownRef.current &&
                    !dropdownRef.current.contains(event.target) &&
                    buttonRef.current &&
                    !buttonRef.current.contains(event.target)
               ) {
                    setShowUserDropdown(false);
               }
          };

          document.addEventListener('mousedown', handleClickOutside);
          return () => {
               document.removeEventListener('mousedown', handleClickOutside);
          };
     }, []);

     const handleLogout = () => {
          setShowUserDropdown(false);
          sessionStorage.clear();
          localStorage.clear();
          logout();
     };

     // Name: Prefer employee_name, then fname, then comp_name, then fallback
     const displayName = profile?.employee_name || profile?.fname || profile?.comp_name || 'Employee';
     // ID: Prefer employee_code, then emp_code, then fallback
     const displayId = profile?.employee_code || profile?.emp_code || 'N/A';

     return (
          <div className="top-navbar" style={{
               display: 'flex',
               alignItems: 'center',
               justifyContent: 'space-between',
               padding: isMobile ? '8px 12px' : '12px 24px',
               backgroundColor: '#ffffff',
               borderBottom: '1px solid #e5e7eb',
               boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
               position: 'sticky',
               top: 0,
               zIndex: 1000,
               gap: isMobile ? '8px' : '16px'
          }}>
               {/* Left Section - Menu Toggle */}
               <div className="navbar-left">
                    <button
                         className="sidebar-toggle-btn"
                         onClick={onToggleSidebar}
                         style={{
                              background: 'none',
                              border: 'none',
                              fontSize: isMobile ? '24px' : '20px',
                              cursor: 'pointer',
                              padding: isMobile ? '4px' : '8px',
                              color: '#334155',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                         }}
                    >
                         <Menu size={isMobile ? 24 : 20} />
                    </button>
               </div>

               {/* Center Section - Title */}
               <div className="navbar-center" style={{ flex: 1, textAlign: 'center' }}>
                    <h2 style={{
                         margin: 0,
                         fontSize: isMobile ? '16px' : '22px',
                         color: '#1e293b',
                         fontWeight: '600',
                         letterSpacing: '0.5px',
                         whiteSpace: 'nowrap',
                         overflow: 'hidden',
                         textOverflow: 'ellipsis'
                    }}>
                         VDC HRMS
                    </h2>
               </div>

               {/* Right Section - Sessions & User Menu */}
               <div className="navbar-right" style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: isMobile ? '8px' : '16px',
                    flexShrink: 0
               }}>
                    {/* Total Sessions - Hide on very small screens */}
                    {role !== 'HR_RADIOLOGY' && !isMobile && (
                         <div style={{
                              fontSize: '13px',
                              color: '#6b7280',
                              whiteSpace: 'nowrap'
                         }}>
                              <small>Sessions: {totalSessions}</small>
                         </div>
                    )}

                    {/* User Menu */}
                    <div className="user-menu" style={{ position: 'relative' }}>
                         <button
                              ref={buttonRef}
                              onClick={() => setShowUserDropdown(!showUserDropdown)}
                              className="user-menu-btn"
                              style={{
                                   background: 'none',
                                   border: '1px solid #e5e7eb',
                                   borderRadius: '8px',
                                   padding: isMobile ? '6px 10px' : '8px 12px',
                                   cursor: 'pointer',
                                   display: 'flex',
                                   alignItems: 'center',
                                   gap: '8px',
                                   transition: 'all 0.2s',
                                   maxWidth: isMobile ? '120px' : 'none'
                              }}
                              onMouseEnter={(e) => {
                                   if (!isMobile) {
                                        e.currentTarget.style.backgroundColor = '#f3f4f6';
                                        e.currentTarget.style.borderColor = '#d1d5db';
                                   }
                              }}
                              onMouseLeave={(e) => {
                                   if (!isMobile) {
                                        e.currentTarget.style.backgroundColor = 'transparent';
                                        e.currentTarget.style.borderColor = '#e5e7eb';
                                   }
                              }}
                         >
                              <User size={isMobile ? 16 : 18} color="#6b7280" />
                              {!isMobile && (
                                   <div className="user-info-container" style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'flex-start'
                                   }}>
                                        <span style={{
                                             fontSize: '14px',
                                             fontWeight: '500',
                                             color: '#1f2937',
                                             maxWidth: '150px',
                                             overflow: 'hidden',
                                             textOverflow: 'ellipsis',
                                             whiteSpace: 'nowrap'
                                        }}>
                                             {displayName}
                                        </span>
                                        <small style={{
                                             fontSize: '11px',
                                             color: '#9ca3af'
                                        }}>
                                             {displayId}
                                        </small>
                                   </div>
                              )}
                         </button>

                         {/* Dropdown Menu */}
                         {showUserDropdown && (
                              <div
                                   ref={dropdownRef}
                                   className="user-dropdown"
                                   style={{
                                        position: 'absolute',
                                        top: '100%',
                                        right: 0,
                                        marginTop: '8px',
                                        width: isMobile ? '200px' : '220px',
                                        backgroundColor: 'white',
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                        borderRadius: '8px',
                                        padding: isMobile ? '12px' : '16px',
                                        zIndex: 1001,
                                        border: '1px solid #e5e7eb'
                                   }}
                              >
                                   {/* User Info */}
                                   <div className="user-info" style={{ marginBottom: '12px', paddingBottom: '12px', borderBottom: '1px solid #f3f4f6' }}>
                                        <div style={{
                                             fontWeight: '600',
                                             fontSize: isMobile ? '13px' : '14px',
                                             color: '#1f2937',
                                             marginBottom: '4px',
                                             overflow: 'hidden',
                                             textOverflow: 'ellipsis',
                                             whiteSpace: 'nowrap'
                                        }}>
                                             {displayName}
                                        </div>
                                        <div style={{
                                             fontSize: '11px',
                                             color: '#6b7280'
                                        }}>
                                             ID: {displayId}
                                        </div>
                                        {/* Show sessions in mobile dropdown */}
                                        {isMobile && role !== 'HR_RADIOLOGY' && (
                                             <div style={{
                                                  fontSize: '11px',
                                                  color: '#6b7280',
                                                  marginTop: '4px'
                                             }}>
                                                  Sessions: {totalSessions}
                                             </div>
                                        )}
                                   </div>

                                   {/* Dropdown Actions */}
                                   <div className="dropdown-actions">
                                        <button
                                             onClick={() => {
                                                  setShowUserDropdown(false);
                                                  navigate('/profile');
                                             }}
                                             style={{
                                                  width: '100%',
                                                  padding: isMobile ? '6px 10px' : '8px 12px',
                                                  marginBottom: '6px',
                                                  backgroundColor: 'transparent',
                                                  border: '1px solid #e5e7eb',
                                                  borderRadius: '6px',
                                                  cursor: 'pointer',
                                                  fontSize: isMobile ? '13px' : '14px',
                                                  color: '#374151',
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  gap: '8px',
                                                  transition: 'background-color 0.2s'
                                             }}
                                             onMouseEnter={(e) => e.target.style.backgroundColor = '#f3f4f6'}
                                             onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                                        >
                                             <User size={14} />
                                             Profile
                                        </button>

                                        <button
                                             onClick={() => {
                                                  setShowUserDropdown(false);
                                                  navigate('/changepassword');
                                             }}
                                             style={{
                                                  width: '100%',
                                                  padding: isMobile ? '6px 10px' : '8px 12px',
                                                  marginBottom: '8px',
                                                  backgroundColor: 'transparent',
                                                  border: '1px solid #e5e7eb',
                                                  borderRadius: '6px',
                                                  cursor: 'pointer',
                                                  fontSize: isMobile ? '13px' : '14px',
                                                  color: '#374151',
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  gap: '8px',
                                                  transition: 'background-color 0.2s'
                                             }}
                                             onMouseEnter={(e) => e.target.style.backgroundColor = '#dbeafe'}
                                             onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                                        >
                                             <Settings size={14} />
                                             Change Password
                                        </button>

                                        <button
                                             onClick={handleLogout}
                                             style={{
                                                  width: '100%',
                                                  padding: isMobile ? '6px 10px' : '8px 12px',
                                                  backgroundColor: '#ef4444',
                                                  color: 'white',
                                                  border: 'none',
                                                  borderRadius: '6px',
                                                  cursor: 'pointer',
                                                  fontSize: isMobile ? '13px' : '14px',
                                                  fontWeight: '500',
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  gap: '8px',
                                                  transition: 'background-color 0.2s'
                                             }}
                                             onMouseEnter={(e) => e.target.style.backgroundColor = '#dc2626'}
                                             onMouseLeave={(e) => e.target.style.backgroundColor = '#ef4444'}
                                        >
                                             <LogOut size={14} />
                                             Logout
                                        </button>
                                   </div>
                              </div>
                         )}
                    </div>
               </div>
          </div>
     );
};

export default TopNavbar;